import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FeedbackDialogComponent } from '@jfw/angular/shared/common-components/feedback';
import {
  catchError,
  combineLatest,
  filter,
  map,
  of,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth/auth.service';
import { DealerUserService } from '../../services/dealer-user/dealer-user.service';
import { ModuleAccessService } from '../../services/module-access/module-access.service';
import { HeaderDesktopComponent } from './desktop/desktop.component';
import { HeaderMobileComponent } from './mobile/mobile.component';

interface DealerIdForm {
  dealerId: FormControl<string>;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HeaderMobileComponent, HeaderDesktopComponent],
})
export class HeaderComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly dealerUserService = inject(DealerUserService);
  private readonly moduleAccessService = inject(ModuleAccessService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  public readonly isMobile = input.required<boolean>();

  protected readonly headerLogo = signal('');
  protected readonly headerTheme = signal('');
  protected readonly servedEnvironment = environment.envVersion;
  protected readonly enableDealerReports = environment.enableDealerReports;
  // EXT-6239: off in prod until the feature is announced, and shown only on
  // the new Dealer Portal site (not the legacy Referral Dashboard).
  protected readonly enableFeedback = environment.enableDealerFeedback;

  protected readonly newSite = toSignal(this.moduleAccessService.newSite$, {
    initialValue: false,
  });
  protected readonly moduleGroups = toSignal(
    this.moduleAccessService.moduleAccessGroups$,
    { initialValue: null },
  );

  protected readonly dealerIdForm = new FormGroup<DealerIdForm>({
    dealerId: new FormControl<string>('', { nonNullable: true }),
  });

  protected readonly newDefaultDealerId$ =
    this.dealerUserService.newDefaultDealerId$;

  private readonly setDefaultDealerResponse$ = this.newDefaultDealerId$.pipe(
    switchMap((defaultDealerId) =>
      combineLatest([
        this.dealerUserService.currentDealerUser$.pipe(
          map((currentDealerUser) => currentDealerUser?.documentId),
          filter((dealerDocumentId) => dealerDocumentId !== undefined),
        ),
        of(defaultDealerId),
      ]).pipe(
        take(1),
        switchMap(([dealerDocumentId, defaultDealerId]) =>
          this.dealerUserService
            .setDefaultDealer(dealerDocumentId, defaultDealerId)
            .pipe(
              catchError((error) => {
                console.error(error);
                this.dealerUserService.defaultDealerSwitchFailed$.next();
                this.snackBar.open(
                  'There was an error switching to the selected dealer.',
                  'Dismiss',
                );
                return this.dealerUserService.currentDealer$.pipe(
                  tap((currentDealer) => {
                    if (currentDealer) {
                      this.dealerIdForm.patchValue({
                        dealerId: currentDealer.dealerId,
                      });
                    }
                  }),
                  map(() => null),
                );
              }),
            ),
        ),
      ),
    ),
    startWith(null),
  );

  protected readonly setDefaultDealerResponse = toSignal(
    this.setDefaultDealerResponse$,
  );

  protected readonly displayName = toSignal(
    this.dealerUserService.firebaseUser$.pipe(
      map((user) => user?.displayName ?? ''),
    ),
    { initialValue: '' },
  );

  protected readonly firebaseUser = toSignal(this.authService.firebaseUser$, {
    initialValue: null,
  });

  protected readonly currentDealer = toSignal(
    this.dealerUserService.currentDealer$.pipe(
      tap((currentDealer) =>
        this.dealerIdForm.patchValue({ dealerId: currentDealer?.dealerId }),
      ),
    ),
    { initialValue: null },
  );

  protected readonly userDealers = toSignal(
    this.dealerUserService.userDealers$,
    {
      initialValue: [],
    },
  );

  ngOnInit(): void {
    if (localStorage.getItem('headerTheme') === 'black-tie') {
      this.headerTheme.set('black-tie');
      this.headerLogo.set('_inverted');
    }
  }

  /** EXT-6239: the shared feedback dialog, same as Platform Portal's. */
  protected onOpenFeedback(): void {
    this.dialog.open(FeedbackDialogComponent, {
      width: '680px',
      maxWidth: '95vw',
      autoFocus: 'first-tabbable',
      restoreFocus: true,
    });
  }
}
